import type { OneJogConfig, PurchaseEvent, PurchaseItem } from '../types';
import { recordPurchase } from './tracking';
import { sendPurchaseToGA } from './analytics';
import { logAIActivity } from './aiActivityLogger';
import { analyzeTextForAttacks, logSecurityEvent, isRateLimitedForSecurity } from './securityAnalyzer';

/** DataLayer ecommerce estándar (GA / GTM) */
interface DataLayerEcommerce {
  event?: string;
  ecommerce?: {
    transaction_id?: string;
    value?: number;
    currency?: string;
    items?: Array<{
      item_id?: string;
      item_name?: string;
      price?: number;
      quantity?: number;
      image?: string;
      item_link?: string;
    }>;
  };
}

function mapDataLayerToPurchase(dl: DataLayerEcommerce, platform: string): PurchaseEvent | null {
  const ec = dl.ecommerce;
  if (!ec?.transaction_id || !Array.isArray(ec.items) || ec.items.length === 0) return null;

  const items: PurchaseItem[] = ec.items.map((i) => ({
    item_id: String(i.item_id ?? ''),
    item_name: String(i.item_name ?? ''),
    price: Number(i.price ?? 0),
    quantity: Number(i.quantity ?? 1),
    image_url: i.image,
    product_url: i.item_link,
  }));

  return {
    transaction_id: ec.transaction_id,
    value: Number(ec.value ?? 0),
    currency: String(ec.currency ?? 'USD'),
    items,
    platform,
  };
}

/** Detecta plataforma por URL o meta */
function detectPlatform(): 'woocommerce' | 'shopify' | 'jumpseller' | 'other' {
  if (typeof window === 'undefined') return 'other';
  const u = window.location.href.toLowerCase();
  const body = document.body?.innerHTML || '';
  if (u.includes('/checkout') && (body.includes('woocommerce') || body.includes('wc-') || document.querySelector('[data-woocommerce]'))) return 'woocommerce';
  if (u.includes('shopify') || document.querySelector('[data-shopify]') || body.includes('Shopify.theme')) return 'shopify';
  if (u.includes('jumpseller') || body.includes('jumpseller')) return 'jumpseller';
  return 'other';
}

const platform = detectPlatform();

/**
 * Observa el dataLayer (push) para eventos 'purchase' / 'ecommerce.purchase'
 * y registra en Firebase + reenvía a GA si aplica.
 */
export function observeDataLayer(siteId: string, config: OneJogConfig): void {
  const dataLayer = (window as unknown as { dataLayer?: DataLayerEcommerce[] }).dataLayer;
  if (!Array.isArray(dataLayer)) return;

  const originalPush = dataLayer.push.bind(dataLayer);
  dataLayer.push = function push(...args: DataLayerEcommerce[]) {
    const result = originalPush(...args);
    for (const dl of args) {
      if (dl?.event === 'purchase' || (dl?.ecommerce && 'transaction_id' in (dl.ecommerce || {}))) {
        const event = mapDataLayerToPurchase(dl, platform);
        if (event) {
          recordPurchase(siteId, event);
          if (config.gaMeasurementId) sendPurchaseToGA(config, event);
          logAIActivity(siteId, {
            promptOrActivity: `Compra: ${event.transaction_id} - ${event.items.length} producto(s)`,
            eventType: 'purchase',
            metadata: {
              transaction_id: event.transaction_id,
              value: event.value,
              currency: event.currency,
              items_count: event.items.length,
            },
          });

          // Analizar nombres de productos por si contienen payloads maliciosos
          const names = event.items.map((i) => i.item_name).join(' | ');
          const analysis = analyzeTextForAttacks(names);
          if (analysis.isSuspicious && !isRateLimitedForSecurity()) {
            void logSecurityEvent(siteId, {
              eventType: analysis.type ?? 'suspicious_pattern',
              severity: analysis.severity ?? 'medium',
              source: 'purchase',
              sample: names.slice(0, 300),
              metadata: { reasons: analysis.reasons, transaction_id: event.transaction_id },
            });
          }
        }
      }
    }
    return result;
  };

  // Revisar eventos ya existentes en dataLayer
  for (const dl of dataLayer) {
      if (dl?.event === 'purchase' || dl?.ecommerce?.transaction_id) {
        const event = mapDataLayerToPurchase(dl, platform);
        if (event) {
          recordPurchase(siteId, event);
          logAIActivity(siteId, {
            promptOrActivity: `Compra: ${event.transaction_id} - ${event.items.length} producto(s)`,
            eventType: 'purchase',
            metadata: { transaction_id: event.transaction_id, value: event.value, items_count: event.items.length },
          });

          const names = event.items.map((i) => i.item_name).join(' | ');
          const analysis = analyzeTextForAttacks(names);
          if (analysis.isSuspicious && !isRateLimitedForSecurity()) {
            void logSecurityEvent(siteId, {
              eventType: analysis.type ?? 'suspicious_pattern',
              severity: analysis.severity ?? 'medium',
              source: 'purchase',
              sample: names.slice(0, 300),
              metadata: { reasons: analysis.reasons, transaction_id: event.transaction_id },
            });
          }
        }
      }
    }
}

/**
 * Para WooCommerce / Shopify / Jumpseller: intenta detectar la página de "gracias por tu compra"
 * y extraer datos del DOM o de variables globales para registrar la compra.
 */
export function tryCaptureThankYouPage(siteId: string, config: OneJogConfig): void {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '';

  // Rutas típicas de confirmación
  const isThankYou =
    /order-received|thank-you|thankyou|checkout\/success|orders\/confirmation/i.test(path) ||
    /order-received|order_key|order_id/i.test(url);

  if (!isThankYou) return;

  // Ya lo maneja dataLayer en muchos temas
  setTimeout(() => {
    const dataLayer = (window as unknown as { dataLayer?: DataLayerEcommerce[] }).dataLayer;
    if (Array.isArray(dataLayer)) {
      for (const dl of dataLayer) {
        if (dl?.event === 'purchase' && dl?.ecommerce) {
          const event = mapDataLayerToPurchase(dl, platform);
          if (event) {
            recordPurchase(siteId, event);
            if (config.gaMeasurementId) sendPurchaseToGA(config, event);
          }
          return;
        }
      }
    }

    // Fallback: leer de meta/JSON en la página (WooCommerce a veces inyecta order data)
    const orderMeta = document.querySelector('script[type="application/ld+json"]');
    if (orderMeta?.textContent) {
      try {
        const json = JSON.parse(orderMeta.textContent);
        const order = Array.isArray(json) ? json.find((o: { '@type'?: string }) => o['@type'] === 'Order') : json['@type'] === 'Order' ? json : null;
        if (order?.orderNumber && order?.orderedItem) {
          const items = Array.isArray(order.orderedItem) ? order.orderedItem : [order.orderedItem];
          const purchaseEvent: PurchaseEvent = {
            transaction_id: String(order.orderNumber),
            value: Number(order.totalPrice?.value ?? 0),
            currency: order.totalPrice?.currencyCode ?? 'USD',
            items: items.map((i: { sku?: string; name?: string; price?: number }) => ({
              item_id: i.sku ?? '',
              item_name: i.name ?? '',
              price: i.price ?? 0,
              quantity: 1,
            })),
            platform,
          };
          recordPurchase(siteId, purchaseEvent);
          if (config.gaMeasurementId) sendPurchaseToGA(config, purchaseEvent);
          logAIActivity(siteId, {
            promptOrActivity: `Compra: ${purchaseEvent.transaction_id} - ${purchaseEvent.items.length} producto(s)`,
            eventType: 'purchase',
            metadata: { transaction_id: purchaseEvent.transaction_id, value: purchaseEvent.value },
          });

          const names = purchaseEvent.items.map((i) => i.item_name).join(' | ');
          const analysis = analyzeTextForAttacks(names);
          if (analysis.isSuspicious && !isRateLimitedForSecurity()) {
            void logSecurityEvent(siteId, {
              eventType: analysis.type ?? 'suspicious_pattern',
              severity: analysis.severity ?? 'medium',
              source: 'purchase',
              sample: names.slice(0, 300),
              metadata: { reasons: analysis.reasons, transaction_id: purchaseEvent.transaction_id },
            });
          }
        }
      } catch {
        // ignore
      }
    }
  }, 1500);
}
